import { useState } from 'react';
import { submitFeedback, FeedbackInput } from '../services/api';

interface FeedbackFormProps {
  resultId: string;
}

export function FeedbackForm({ resultId }: FeedbackFormProps) {
  const [overallAccuracy, setOverallAccuracy] = useState<string>('');
  const [accurateDimensions, setAccurateDimensions] = useState<string[]>([]);
  const [inaccurateDimensions, setInaccurateDimensions] = useState<string[]>([]);
  const [clarity, setClarity] = useState<string>('');
  const [coverage, setCoverage] = useState<string>('');
  const [comments, setComments] = useState<string>('');

  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');

  const handleDimensionToggle = (
    dimension: string,
    current: string[],
    setter: (value: string[]) => void,
    exclusive?: string // 互斥选项，如 "all_similar"
  ) => {
    if (dimension === exclusive) {
      // 选择互斥选项时，清除其他选择
      setter(current.includes(dimension) ? [] : [dimension]);
    } else {
      // 选择具体维度时，清除互斥选项
      const filtered = current.filter(d => d !== exclusive);
      if (filtered.includes(dimension)) {
        setter(filtered.filter(d => d !== dimension));
      } else {
        setter([...filtered, dimension]);
      }
    }
  };

  const handleSubmit = async () => {
    if (!overallAccuracy) return;

    setSubmitStatus('submitting');

    const input: FeedbackInput = {
      resultId,
      overallAccuracy: overallAccuracy as FeedbackInput['overallAccuracy'],
      accurateDimensions: accurateDimensions.length > 0 ? accurateDimensions : undefined,
      inaccurateDimensions: inaccurateDimensions.length > 0 ? inaccurateDimensions : undefined,
      clarity: clarity ? clarity as FeedbackInput['clarity'] : undefined,
      coverage: coverage ? coverage as FeedbackInput['coverage'] : undefined,
      comments: comments.trim() || undefined,
    };

    const response = await submitFeedback(input);

    if (response.success) {
      setSubmitStatus('submitted');
    } else {
      setSubmitStatus('error');
    }
  };

  if (submitStatus === 'submitted') {
    return (
      <div className="bg-white rounded-xl border border-blue-100 p-6">
        <div className="text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">感谢您的反馈！</h3>
          <p className="text-gray-600">您的意见将帮助我们改进测评系统</p>
        </div>
      </div>
    );
  }

  const dimensions = [
    { id: 'D1', label: 'D1 锚定来源' },
    { id: 'D2', label: 'D2 处理模式' },
    { id: 'D3', label: 'D3 驱动来源' },
    { id: 'D4', label: 'D4 不确定性态度' },
  ];

  return (
    <div className="bg-white rounded-xl border border-blue-100 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">结果反馈</h2>

      <div className="space-y-6">
        {/* 1. 整体一致性（必填） */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            测试结果与您的自我认知是否一致？<span className="text-red-500">*</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'very_match', label: '非常符合' },
              { value: 'mostly_match', label: '基本符合' },
              { value: 'partly_match', label: '部分符合' },
              { value: 'not_quite', label: '不太符合' },
              { value: 'not_at_all', label: '完全不符合' },
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setOverallAccuracy(option.value)}
                className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                  overallAccuracy === option.value
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'border-gray-200 text-gray-700 hover:border-blue-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* 2. 最准确的维度（选填） */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            哪些维度的描述最符合您？（可多选）
          </label>
          <div className="flex flex-wrap gap-2">
            {dimensions.map(dim => (
              <button
                key={dim.id}
                onClick={() => handleDimensionToggle(dim.id, accurateDimensions, setAccurateDimensions, 'all_similar')}
                className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                  accurateDimensions.includes(dim.id)
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'border-gray-200 text-gray-700 hover:border-blue-300'
                }`}
              >
                {dim.label}
              </button>
            ))}
            <button
              onClick={() => handleDimensionToggle('all_similar', accurateDimensions, setAccurateDimensions, 'all_similar')}
              className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                accurateDimensions.includes('all_similar')
                  ? 'bg-blue-50 border-blue-500 text-blue-700'
                  : 'border-gray-200 text-gray-700 hover:border-blue-300'
              }`}
            >
              都差不多
            </button>
          </div>
        </div>

        {/* 3. 最不准确的维度（选填） */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            哪些维度的描述最不符合您？（可多选）
          </label>
          <div className="flex flex-wrap gap-2">
            {dimensions.map(dim => (
              <button
                key={dim.id}
                onClick={() => handleDimensionToggle(dim.id, inaccurateDimensions, setInaccurateDimensions, 'all_fine')}
                className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                  inaccurateDimensions.includes(dim.id)
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'border-gray-200 text-gray-700 hover:border-blue-300'
                }`}
              >
                {dim.label}
              </button>
            ))}
            <button
              onClick={() => handleDimensionToggle('all_fine', inaccurateDimensions, setInaccurateDimensions, 'all_fine')}
              className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                inaccurateDimensions.includes('all_fine')
                  ? 'bg-blue-50 border-blue-500 text-blue-700'
                  : 'border-gray-200 text-gray-700 hover:border-blue-300'
              }`}
            >
              都还好
            </button>
          </div>
        </div>

        {/* 4. 题目清晰度（选填） */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            题目表述是否清晰易懂？
          </label>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'clear', label: '清晰易懂' },
              { value: 'mostly_clear', label: '基本清晰' },
              { value: 'some_unclear', label: '部分题目难理解' },
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setClarity(clarity === option.value ? '' : option.value)}
                className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                  clarity === option.value
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'border-gray-200 text-gray-700 hover:border-blue-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* 5. 选项覆盖度（选填） */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            题目选项是否能反映您的真实想法？
          </label>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'good_coverage', label: '基本都能找到符合的选项' },
              { value: 'few_mismatch', label: '有少数题目的选项都不太符合我' },
              { value: 'many_mismatch', label: '较多题目的选项都不是我会做的' },
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setCoverage(coverage === option.value ? '' : option.value)}
                className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                  coverage === option.value
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'border-gray-200 text-gray-700 hover:border-blue-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* 6. 开放性意见（选填） */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            如有其他反馈，请在此填写
          </label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="如：哪道题目的选项不符合您的情况、界面使用体验等..."
            maxLength={500}
            rows={4}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:border-blue-500 resize-none"
          />
          <p className="text-right text-sm text-gray-400 mt-1">{comments.length}/500</p>
        </div>

        {/* 提交按钮 */}
        <div>
          {submitStatus === 'error' && (
            <p className="text-red-500 text-sm mb-3">提交失败，请稍后重试</p>
          )}
          <button
            onClick={handleSubmit}
            disabled={!overallAccuracy || submitStatus === 'submitting'}
            className={`w-full px-6 py-3 font-medium rounded-xl transition-colors ${
              overallAccuracy && submitStatus !== 'submitting'
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {submitStatus === 'submitting' ? '提交中...' : '提交反馈'}
          </button>
        </div>
      </div>
    </div>
  );
}
